import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { usePermissions } from '../../lib/permissions';
import { supabase } from '../../lib/supabase';
import EmptyView from '../shared/EmptyView';
import Button from '../../components/Button/Button';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import deleteIcon from '../../image-assets/icon.delete.svg';
import ToneRulesSection from './components/ToneRulesSection';
import BrandToneSection from './components/BrandToneSection';
import ProductContextSection from './components/ProductContextSection';
import { ToneSettings, ToneSection } from './types';
import { useSubscription } from '../../hooks/useSubscription';
import SubscriptionDialog from '../../components/SubscriptionDialog/SubscriptionDialog';
import '../SharedStyles.css';
import './ToneView.css';

const ToneView: React.FC = () => {
  const { selectedTeam } = useWorkspace();
  const { user } = useAuth();
  const { profileData } = useProfile();
  const { isOwner, isAdmin } = usePermissions(selectedTeam, user?.id, profileData?.id);
  const canEdit = isOwner || isAdmin;

  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [toneSettings, setToneSettings] = useState<ToneSettings | null>(null);
  const [editableTone, setEditableTone] = useState<string>('');
  const [editableContext, setEditableContext] = useState<string>('');

  const [toneSections, setToneSections] = useState<ToneSection[]>([]);
  const [isLoadingSections, setIsLoadingSections] = useState(false);
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [, setNewRule] = useState<{sectionId: string, value: string} | null>(null);
  const [, setEditingRuleIndex] = useState<{sectionId: string, index: number} | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setters for rule input UI
  const [, setNewRuleInput] = useState<{sectionId: string, value: string} | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter for rule click UI
  const [, setClickedRuleInfo] = useState<{sectionId: string, index: number} | null>(null);
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();

  const ruleRefs = useRef<{[key: string]: HTMLDivElement}>({});
  
  const getRuleRefKey = (sectionId: string, index: number) => `${sectionId}-${index}`;

  const fetchData = useCallback(async () => {
      if (!selectedTeam) return;

    setIsLoadingSettings(true);
    setIsLoadingSections(true);
    setSettingsError(null);
    setSectionsError(null);

    try {
      const [settingsResult, sectionsResult] = await Promise.all([
        supabase
          .from('Teams')
          .select('tov_tone, tov_product_context')
          .eq('id', selectedTeam.id)
          .single(),
        supabase
          .from('ToneSections')
          .select('*')
          .eq('team_id', selectedTeam.id)
      ]);

      if (settingsResult.error) throw new Error(`Settings fetch failed: ${settingsResult.error.message}`);
      if (settingsResult.data) {
        const fetchedSettings = {
          tov_tone: settingsResult.data.tov_tone,
          tov_product_context: settingsResult.data.tov_product_context
        };
        setToneSettings(fetchedSettings);
        setEditableTone(settingsResult.data.tov_tone || '');
        setEditableContext(settingsResult.data.tov_product_context || '');
      } else {
        setToneSettings({ tov_tone: null, tov_product_context: null });
        setEditableTone('');
        setEditableContext('');
      }

      if (sectionsResult.error) throw new Error(`Sections fetch failed: ${sectionsResult.error.message}`);
      if (sectionsResult.data) {
        setToneSections(sectionsResult.data);
      } else {
        setToneSections([]);
      }

    } catch (err: any) {
      console.error("Error fetching tone data:", err);
      setSettingsError(err.message);
      setSectionsError(err.message);
      setEditableTone('');
      setEditableContext('');
      setToneSections([]);
    } finally {
      setIsLoadingSettings(false);
      setIsLoadingSections(false);
    }
  }, [selectedTeam]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveTone = async (value: string) => {
    if (!selectedTeam || !canEdit) return;

    if (!isSubscriptionLoading && !hasActiveSubscription) {
      setIsSubscriptionDialogOpen(true);
      return;
    }

    setIsSaving(true);
    setSettingsError(null);

    try {
      const { error: updateError } = await supabase
        .from('Teams')
        .update({
          tov_tone: value.trim() || null
        })
        .eq('id', selectedTeam.id);

      if (updateError) throw updateError;

      setToneSettings(prev => prev ? { 
        ...prev, 
        tov_tone: value.trim() || null 
      } : {
        tov_tone: value.trim() || null,
        tov_product_context: null
      });

    } catch (err: any) {
      console.error("Error saving brand tone:", err);
      setSettingsError(`Failed to save brand tone: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveContext = async (value: string) => {
    if (!selectedTeam || !canEdit) return;

    if (!isSubscriptionLoading && !hasActiveSubscription) {
      setIsSubscriptionDialogOpen(true);
      return;
    }

    setIsSaving(true);
    setSettingsError(null);

    try {
      const { error: updateError } = await supabase
        .from('Teams')
        .update({
          tov_product_context: value.trim() || null
        })
        .eq('id', selectedTeam.id);

      if (updateError) throw updateError;

      setToneSettings(prev => prev ? { 
        ...prev, 
        tov_product_context: value.trim() || null 
      } : {
        tov_tone: null,
        tov_product_context: value.trim() || null
      });

    } catch (err: any) {
      console.error("Error saving product context:", err);
      setSettingsError(`Failed to save product context: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSection = async () => {
    if (!selectedTeam || !canEdit) return;

    setIsAddingSection(true);
    setSectionsError(null);

    try {
      const { data: newSection, error: insertError } = await supabase
        .from('ToneSections')
        .insert({
          team_id: selectedTeam.id,
          title: 'Untitled Section',
          description: '',
          rules: [],
        })
        .select()
          .single();

      if (insertError) throw insertError;

      if (newSection) {
        setToneSections(prevSections => [...prevSections, newSection]);
      } else {
        throw new Error("Failed to retrieve the new section after creation.");
      }

    } catch (err: any) {
      console.error("Error adding section:", err);
      setSectionsError(`Failed to add section: ${err.message}`);
    } finally {
      setIsAddingSection(false);
    }
  };

  const handleDeleteSection = async () => {
    if (!sectionToDelete || !canEdit) return;

    setIsDeleting(true);
    setSectionsError(null);

    try {
      const { error: deleteError } = await supabase
        .from('ToneSections')
        .delete()
        .eq('id', sectionToDelete);

      if (deleteError) throw deleteError;

      setToneSections(prevSections => 
        prevSections.filter(section => section.id !== sectionToDelete)
      );
    } catch (err: any) {
      console.error("Error deleting section:", err);
      setSectionsError(`Failed to delete section: ${err.message}`);
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setSectionToDelete(null);
    }
  };

  const openDeleteDialog = (sectionId: string) => {
    setSectionToDelete(sectionId);
    setDeleteDialogOpen(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- section input handler
  const handleSectionInputChange = (
    sectionId: string,
    field: 'title' | 'description' | 'rules',
    value: string
  ) => {
    setToneSections(prevSections =>
      prevSections.map(section => {
        if (section.id === sectionId) {
          if (field === 'rules') {
            const rulesArray = value.split('\n').filter(rule => rule.trim() !== '');
            return { ...section, rules: rulesArray };
          } else {
            return { ...section, [field]: value };
          }
        }
        return section;
      })
    );
  };

  const handleKeyDown = (
    sectionId: string,
    field: 'title' | 'description',
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const value = event.currentTarget.innerText;
      
      setToneSections(prevSections =>
        prevSections.map(section => 
          section.id === sectionId ? { ...section, [field]: value } : section
        )
      );
      
      event.currentTarget.blur();
    }
  };

  const handleSaveSection = async (
    sectionId: string, 
    field: 'title' | 'description' | 'rules', 
    value: string
  ) => {
    if (!canEdit) return;

    const sectionToSave = toneSections.find(section => section.id === sectionId);
    if (!sectionToSave) {
      console.error("Section not found in state for saving:", sectionId);
      return;
    }

    const updatedSection = { ...sectionToSave };
    if (field === 'title' || field === 'description') {
      updatedSection[field] = value;
    } else if (field === 'rules') {
      updatedSection.rules = value.split('\n').filter(rule => rule.trim() !== '');
    }

    setToneSections(prevSections =>
      prevSections.map(section => 
        section.id === sectionId ? updatedSection : section
      )
    );

    try {
      const { error: updateError } = await supabase
        .from('ToneSections')
        .update({
          title: updatedSection.title,
          description: updatedSection.description,
          rules: updatedSection.rules,
        })
        .eq('id', sectionId);

      if (updateError) throw updateError;

    } catch (err: any) {
      console.error(`Error saving section ${sectionId}:`, err);
      setSectionsError(`Failed to save section "${updatedSection.title}": ${err.message}`);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- add rule handler
  const handleAddRule = (sectionId: string) => {
    setToneSections(prevSections =>
      prevSections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            rules: [...section.rules, '']
          };
        }
        return section;
      })
    );
    
    const section = toneSections.find(s => s.id === sectionId);
    if (section) {
      setNewRule({sectionId, value: ''});
      setTimeout(() => {
        setEditingRuleIndex({sectionId, index: section.rules.length});
      }, 0);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- rule change handler
  const handleRuleChange = (sectionId: string, index: number, value: string) => {
    setToneSections(prevSections =>
      prevSections.map(section => {
        if (section.id === sectionId) {
          const updatedRules = [...section.rules];
          updatedRules[index] = value;
          return {
            ...section,
            rules: updatedRules
          };
        }
        return section;
      })
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- rule click handler
  const handleRuleClick = (sectionId: string, index: number, currentValue: string) => {
    if (!canEdit || isAddingSection) return;
    
    setEditingRuleIndex({ sectionId, index });
    
    setTimeout(() => {
      const refKey = getRuleRefKey(sectionId, index);
      const ruleElement = ruleRefs.current[refKey];
      if (ruleElement) {
        ruleElement.focus();
        
        if (document.createRange && window.getSelection) {
          const range = document.createRange();
          range.selectNodeContents(ruleElement);
          range.collapse(false);
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }
    }, 10);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- rule keydown handler
  const handleRuleKeyDown = (
    sectionId: string,
    index: number,
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const value = event.currentTarget.innerText;
      
      setToneSections(prevSections =>
        prevSections.map(section => {
          if (section.id === sectionId) {
            const updatedRules = [...section.rules];
            updatedRules[index] = value;
            return { ...section, rules: updatedRules };
          }
          return section;
        })
      );
      
      event.currentTarget.blur();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- rule blur handler
  const handleRuleBlur = async (
    sectionId: string, 
    index: number, 
    value: string,
    isClickingAnotherRule = false
  ) => {
    if (!canEdit) return;

    const section = toneSections.find(s => s.id === sectionId);
    if (!section) return;

    const updatedRules = [...section.rules];
    updatedRules[index] = value;

    setToneSections(prevSections =>
      prevSections.map(section => {
        if (section.id === sectionId) {
          return { ...section, rules: updatedRules };
        }
        return section;
      })
    );

    if (!isClickingAnotherRule) {
      setEditingRuleIndex(null);
    }
    
    setNewRule(null);

    try {
      const { error: updateError } = await supabase
        .from('ToneSections')
        .update({
          rules: updatedRules,
        })
        .eq('id', sectionId);

      if (updateError) throw updateError;
    } catch (err: any) {
      console.error(`Error saving rule ${index} in section ${sectionId}:`, err);
      setSectionsError(`Failed to save rule: ${err.message}`);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- delete rule handler
  const handleDeleteRule = async (sectionId: string, ruleIndex: number) => {
    if (!canEdit) return;

    const section = toneSections.find(s => s.id === sectionId);
    if (!section) return;

    const updatedRules = section.rules.filter((_, index) => index !== ruleIndex);
    
    setToneSections(prevSections =>
      prevSections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            rules: updatedRules
          };
        }
        return section;
      })
    );

    try {
      const { error: updateError } = await supabase
        .from('ToneSections')
        .update({
          rules: updatedRules,
        })
        .eq('id', sectionId);

      if (updateError) throw updateError;
    } catch (err: any) {
      console.error(`Error deleting rule from section ${sectionId}:`, err);
      setSectionsError(`Failed to delete rule: ${err.message}`);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- add rule from input handler
  const handleAddRuleFromInput = (sectionId: string, value: string) => {
    if (!value.trim()) return;
    
    const section = toneSections.find(s => s.id === sectionId);
    if (!section) return;
    
    const updatedRules = [...section.rules, value.trim()];
    
    setToneSections(prevSections =>
      prevSections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            rules: updatedRules
          };
        }
        return section;
      })
    );
    
    saveRulesToDatabase(sectionId, updatedRules);
  };

  const saveRulesToDatabase = async (sectionId: string, rules: string[]) => {
    try {
      const { error: updateError } = await supabase
        .from('ToneSections')
        .update({
          rules: rules,
        })
        .eq('id', sectionId);

      if (updateError) throw updateError;
    } catch (err: any) {
      console.error(`Error saving rules for section ${sectionId}:`, err);
      setSectionsError(`Failed to save rules: ${err.message}`);
    }
  };

  const handleSectionError = (error: string | null) => {
    setSectionsError(error);
  };

  const isInitialLoading = isLoadingSettings || isLoadingSections;
  const combinedError = settingsError || sectionsError;

  if (!selectedTeam || (isInitialLoading && !toneSettings && toneSections.length === 0)) {
    return (
      <div className="view-container">
        <div className="loading-container flex justify-center items-center h-full">
          <Button
            variant="text"
            size="large"
            isLoading={true}
            disabled={true}
          >
          </Button>
        </div>
      </div>
    );
  }

  if (combinedError && !isSaving && !isAddingSection) {
    return <EmptyView message={`Error loading data: ${combinedError}`} />;
  }

  return (
    <div className="view-container tone-container">
      <div className="content">
        {settingsError && (
          <div className="error-banner mb-4">
            <p>Error saving settings: {settingsError}</p>
          </div>
        )}
        
        <div className="sections-list">
          <ProductContextSection
            value={editableContext}
            onChange={(value) => {
              setEditableContext(value);
              handleSaveContext(value);
            }}
            disabled={isSaving || !canEdit || isAddingSection}
            teamId={selectedTeam?.id}
          />
          
          <BrandToneSection
            value={editableTone}
            onChange={(value) => {
              setEditableTone(value);
              handleSaveTone(value);
            }}
            disabled={isSaving || !canEdit || isAddingSection}
            teamId={selectedTeam?.id}
          />
          
          {isLoadingSections && (
            <div className="loading-state"><p>Loading rules...</p></div>
          )}
          
          {sectionsError && !isLoadingSections && (
            <div className="error-state"><p>{sectionsError}</p></div>
          )}

          {!isLoadingSections && toneSections.map((section) => (
            <div key={section.id} className="tone-section-item">
              <div className="tone-section-header">
                <div
                  className={`tone-section-title ${!section.title || section.title.trim().length === 0 ? 'empty' : ''}`}
                  contentEditable={canEdit && !isAddingSection}
                  suppressContentEditableWarning={true}
                  onKeyDown={(e) => handleKeyDown(section.id, 'title', e)}
                  onBlur={(e) => handleSaveSection(section.id, 'title', e.currentTarget.innerText)}
                  data-placeholder="Untitled Section"
                >
                  {section.title || ''}
                </div>
                
                <div
                  className={`tone-section-description ${!section.description || section.description.trim().length === 0 ? 'empty' : ''}`}
                  contentEditable={canEdit && !isAddingSection}
                  suppressContentEditableWarning={true}
                  onKeyDown={(e) => handleKeyDown(section.id, 'description', e)}
                  onBlur={(e) => handleSaveSection(section.id, 'description', e.currentTarget.innerText)}
                  data-placeholder="Add a description..."
                >
                  {section.description || ''}
                </div>
                
                {canEdit && (
                  <button 
                    className="delete-section-button" 
                    onClick={() => openDeleteDialog(section.id)}
                    aria-label={`Delete section ${section.title}`}
                  >
                    <img src={deleteIcon} alt="Delete" width="16" height="16" />
                  </button>
                )}
              </div>
              
              <ToneRulesSection
                section={section}
                canEdit={canEdit}
                isAddingSection={isAddingSection}
                onSectionError={handleSectionError}
              />
            </div>
          ))}
          
          {canEdit && (
            <div className="add-section-container">
              <Button
                variant="secondary"
                onClick={handleAddSection}
                disabled={isAddingSection || isSaving || isInitialLoading}
                isLoading={isAddingSection}
              >
                Add Section
              </Button>
            </div>
          )}
        </div>
      </div>
      
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteSection}
        message="Are you sure you want to delete this section? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={isDeleting}
      />
      <SubscriptionDialog
        isOpen={isSubscriptionDialogOpen}
        onClose={() => setIsSubscriptionDialogOpen(false)}
      />
    </div>
  );
};

export default ToneView; 